import type { UserResource } from '@clerk/shared/types';

type UnsafeMetadataPatch = Record<string, unknown>;

const pendingWrites = new Map<string, Promise<unknown>>();

function readMetadata(user: UserResource): Record<string, unknown> {
  return (user.unsafeMetadata ?? {}) as Record<string, unknown>;
}

// Clerk's user.update() replaces unsafeMetadata wholesale, so concurrent
// writers must be serialized per user or the later write clobbers the
// earlier one's key. Each write waits for the previous one to settle
// (success or failure) before re-reading unsafeMetadata, since Clerk mutates
// the UserResource in place once a prior update() resolves.
export function updateUnsafeMetadata(
  user: UserResource,
  patch: UnsafeMetadataPatch,
): Promise<UserResource> {
  const previous = pendingWrites.get(user.id) ?? Promise.resolve();

  const next = previous
    .catch(() => undefined)
    .then(() =>
      user.update({
        unsafeMetadata: { ...readMetadata(user), ...patch },
      }),
    );

  pendingWrites.set(user.id, next);
  // .finally()'s own derived promise mirrors next's rejection; swallow it
  // here so a failed write doesn't surface as an unhandled rejection — the
  // caller still observes the failure through the returned `next` promise.
  next
    .finally(() => {
      if (pendingWrites.get(user.id) === next) {
        pendingWrites.delete(user.id);
      }
    })
    .catch(() => undefined);

  return next;
}

export function getPendingWriteCountForTest(): number {
  return pendingWrites.size;
}

export function readUnsafeMetadata<T>(
  user: UserResource | null | undefined,
  key: string,
): T | undefined {
  return (user?.unsafeMetadata as Record<string, unknown> | undefined)?.[key] as
    | T
    | undefined;
}
